import type { Card } from '../../engine/card.js';
import type { GameState, Pile } from '../../engine/game-state.js';
import type { Move } from '../../engine/moves.js';
import type { SolitaireRules } from '../../engine/rules.js';

// 7-row pyramid: 1 + 2 + 3 + 4 + 5 + 6 + 7 = 28 cards, all face-up.
// IDs are `r{row}c{col}`, both 1-indexed.
function buildPyramidIds(): readonly string[] {
  const ids: string[] = [];
  for (let r = 1; r <= 7; r++) {
    for (let c = 1; c <= r; c++) ids.push(`r${r}c${c}`);
  }
  return ids;
}
export const PYRAMID_IDS = buildPyramidIds();

// `COVERS[id]` = the two cards diagonally below this one. While EITHER cover
// still has a card, this card is buried and can't be played.
function buildCovers(): Record<string, readonly string[]> {
  const cov: Record<string, readonly string[]> = {};
  for (let r = 1; r < 7; r++) {
    for (let c = 1; c <= r; c++) {
      cov[`r${r}c${c}`] = [`r${r + 1}c${c}`, `r${r + 1}c${c + 1}`];
    }
  }
  return cov;
}
export const COVERS = buildCovers();

export function isUncovered(state: GameState, pileId: string): boolean {
  const covers = COVERS[pileId];
  if (!covers) return true; // bottom row (r7) is always uncovered
  for (const id of covers) {
    if ((state.piles[id]?.cards.length ?? 0) > 0) return false;
  }
  return true;
}

function emptyPile(id: string, type: Pile['type']): Pile {
  return { id, type, cards: [] };
}

function setupPyramid(deck: Card[]): GameState {
  const piles: Record<string, Pile> = {};
  for (const id of PYRAMID_IDS) piles[id] = emptyPile(id, 'peak');
  piles.stock = emptyPile('stock', 'stock');
  piles.waste = emptyPile('waste', 'waste');
  piles.foundation = emptyPile('foundation', 'foundation');

  const d = deck.slice();
  // 28 cards face-up to the pyramid, in row-major order.
  for (let i = 0; i < 28; i++) {
    const c = d.shift()!;
    c.faceUp = true;
    piles[PYRAMID_IDS[i]!]!.cards.push(c);
  }
  // Remaining 24 face-down to stock.
  for (const c of d) {
    c.faceUp = false;
    piles.stock!.cards.push(c);
  }
  return { piles, movesCount: 0 };
}

/**
 * A card is playable when it's the top of an uncovered pyramid pile or the
 * top of waste. Pyramid piles only ever hold one card at a time, but we
 * still index by top for uniformity with the rest of the engine.
 */
function isPlayableTop(state: GameState, pileId: string, cardId: string): boolean {
  const pile = state.piles[pileId];
  if (!pile) return false;
  const top = pile.cards[pile.cards.length - 1];
  if (!top || top.id !== cardId) return false;
  if (pile.type === 'waste') return true;
  if (pile.type === 'peak') return isUncovered(state, pileId);
  return false;
}

export const pyramidRules: SolitaireRules = {
  id: 'pyramid',

  setup(deck) {
    return setupPyramid(deck);
  },

  canMove(state, move: Move) {
    const from = state.piles[move.from];
    const to = state.piles[move.to];
    if (!from || !to || from === to) return false;

    // Stock → waste: top of stock only.
    if (from.type === 'stock') {
      if (to.id !== 'waste') return false;
      const top = from.cards[from.cards.length - 1];
      return !!top && top.id === move.cardId;
    }

    // Single-card discard to foundation: only Kings (rank 13) can leave the
    // table without a partner. Pair discards happen through the spec, since
    // the engine's Move only carries one card.
    if (to.id !== 'foundation') return false;
    if (!isPlayableTop(state, from.id, move.cardId)) return false;
    const card = from.cards[from.cards.length - 1]!;
    return card.rank === 13;
  },

  isWon(state) {
    for (const id of PYRAMID_IDS) {
      if ((state.piles[id]?.cards.length ?? 0) > 0) return false;
    }
    return true;
  },

  isLost(state) {
    if ((state.piles.stock?.cards.length ?? 0) > 0) return false;
    if (hasAnyMove(state)) return false;
    for (const id of PYRAMID_IDS) {
      if ((state.piles[id]?.cards.length ?? 0) > 0) return true;
    }
    return false;
  },
};

export function drawFromStock(state: GameState): boolean {
  const stock = state.piles.stock!;
  const waste = state.piles.waste!;
  if (stock.cards.length === 0) return false;
  const c = stock.cards.pop()!;
  c.faceUp = true;
  waste.cards.push(c);
  return true;
}

/**
 * Cards currently legal to start a discard from: top of waste + top of every
 * uncovered pyramid pile.
 */
export function playableCards(state: GameState): { card: Card; pileId: string }[] {
  const out: { card: Card; pileId: string }[] = [];
  for (const id of PYRAMID_IDS) {
    const pile = state.piles[id]!;
    if (pile.cards.length === 0) continue;
    if (!isUncovered(state, id)) continue;
    out.push({ card: pile.cards[pile.cards.length - 1]!, pileId: id });
  }
  const waste = state.piles.waste!;
  if (waste.cards.length > 0) {
    out.push({ card: waste.cards[waste.cards.length - 1]!, pileId: 'waste' });
  }
  return out;
}

export function hasAnyMove(state: GameState): boolean {
  const cards = playableCards(state);
  for (const { card } of cards) {
    if (card.rank === 13) return true;
  }
  for (let i = 0; i < cards.length; i++) {
    for (let j = i + 1; j < cards.length; j++) {
      if (cards[i]!.card.rank + cards[j]!.card.rank === 13) return true;
    }
  }
  return false;
}
